import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Linkedin, Share2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const BlogPost = () => {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        fetchPost();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const fetchPost = async () => {
        try {
            const response = await fetch('/.netlify/functions/blog-posts');
            if (response.ok) {
                const data = await response.json();
                // Try to find by slug first, fall back to ID for backward compatibility
                const foundPost = (data.posts || []).find(p => p.slug === slug || p.id === slug);
                setPost(foundPost || null);
            }
        } catch (error) {
            console.error('Error fetching post:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatContent = (content) => {
        if (!content) return '';
        // Split by double newlines for paragraphs
        return content.split('\n\n').map((paragraph, index) => (
            <p key={index} className="mb-6 text-lg leading-relaxed text-gray-300">
                {paragraph}
            </p>
        ));
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: post.title,
                    text: post.excerpt,
                    url: window.location.href
                });
            } catch (error) {
                // User cancelled sharing - this is not an error
                if (error.name !== 'AbortError') {
                    console.error('Share failed:', error);
                }
            }
        } else {
            try {
                await navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
            } catch {
                // Fallback for older browsers
                prompt('Copy this link:', window.location.href);
            }
        }
    };

    if (loading) {
        return (
            <>
                <Navigation scrolled={scrolled} />
                <div className="min-h-screen flex justify-center items-center">
                    <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
            </>
        );
    }

    if (!post) {
        return (
            <>
                <Navigation scrolled={scrolled} />
                <div className="min-h-screen flex flex-col justify-center items-center pt-32">
                    <h1 className="text-3xl font-bold text-white mb-4">Post Not Found</h1>
                    <p className="text-gray-400 mb-8">The blog post you're looking for doesn't exist.</p>
                    <Link
                        to="/blog"
                        className="inline-flex items-center text-primary-400 hover:text-primary-300 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Blog
                    </Link>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navigation scrolled={scrolled} />

            <article className="min-h-screen pt-32 pb-20 relative">
                {/* Background */}
                <div className="absolute inset-0 circuit-pattern opacity-20" />
                <div className="absolute inset-0 grid-pattern opacity-10" />

                <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back link */}
                    <Link
                        to="/blog"
                        className="inline-flex items-center text-primary-400 hover:text-primary-300 transition-colors mb-8"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Blog
                    </Link>

                    {/* Header */}
                    <header className="mb-12">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 gradient-text leading-tight">
                            {post.title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-4 text-secondary mb-6">
                            <span className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2" />
                                {formatDate(post.publishedAt)}
                            </span>
                            <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-2" />
                                {post.readTime || 5} min read
                            </span>
                            {post.linkedinPosted && (
                                <span className="flex items-center text-[#0A66C2]" title="Shared on LinkedIn">
                                    <Linkedin className="w-4 h-4 mr-1" />
                                    LinkedIn
                                </span>
                            )}
                        </div>

                        {/* Share button */}
                        <button
                            onClick={handleShare}
                            className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors"
                        >
                            <Share2 className="w-4 h-4 mr-2" />
                            Share Article
                        </button>
                    </header>

                    {/* Excerpt */}
                    <div className="text-xl text-primary-300 mb-8 p-6 rounded-xl bg-primary-500/5 border border-primary-500/20">
                        {post.excerpt}
                    </div>

                    {/* Content */}
                    <div className="prose prose-lg prose-invert max-w-none">
                        {formatContent(post.content)}
                    </div>

                    {/* Footer CTA */}
                    <div className="mt-16 p-8 rounded-2xl metallic-texture gradient-border text-center">
                        <h3 className="text-2xl font-bold text-white mb-4">
                            Want to improve your QA metrics?
                        </h3>
                        <p className="text-gray-400 mb-6">
                            Discover how IronGate can transform your quality engineering practices.
                        </p>
                        <Link
                            to="/#contact"
                            className="inline-flex items-center px-6 py-3 rounded-lg font-semibold text-white transition-all"
                            style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
                        >
                            Get in Touch
                        </Link>
                    </div>
                </div>
            </article>

            <Footer />
        </>
    );
};

export default BlogPost;
